import { Layout } from '@/components/common/Layout';
import { LoveverySWRConfig } from '@/components/common/LoveverySWRConfig';
import { ChatbotWidget } from '@/components/common/ChatbotWidget';
import { AuthProvider } from '@/context/AuthContext';
import {
  TypeFooter,
  TypeGlobalNav,
  TypeHeader,
  TypePage,
  TypeSubFooter,
} from '@/core/generated-types';
import '@/styles/base.scss';
import '@/styles/globals.scss';
import SEO from '@/utils/next-seo.config';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { NextIntlClientProvider } from 'next-intl';
import { PagesProgressBar as ProgressBar } from 'next-nprogress-bar';
import { usePathname } from 'next/navigation';
import { FeatureProvider } from '@/context/FeatureProvider';
import { CartContextProvider } from '@/context/CartContext';
import { withRouter } from 'next/router';
import { SubscriptionProvider } from '@/context/SubscriptionContext';
import { SubFlowV3Provider } from '@/components/subflow-v3/context/SubFlowV3Context';
import { IS_DEV_ENV } from '@/app/[locale]/constants';
import { startFidesToShopifyBridge } from '@/lib/bridgeFidesToShopify';
import { useEffect } from 'react';
import MoonshotAnalytics from '@/components/analytics/MoonshotAnalytics';

function shouldDisplayProgressBar(url?: string | null) {
  if (!url) return false;
  return url.includes('account');
}

export interface PageProps {
  page: TypePage;
  header: TypeHeader;
  footer: TypeFooter;
  footerLegal: TypeSubFooter;
  globalNav: TypeGlobalNav;
}

function MyApp({ Component, pageProps = {}, router }: AppProps) {
  const pathname = usePathname();

  useEffect(() => {
    const stop = startFidesToShopifyBridge();
    return () => stop?.();
  }, []);

  return (
    <NextIntlClientProvider
      locale={(router.query?.locale as string) || 'en-US'}
      messages={pageProps.messages}
    >
      <LoveverySWRConfig>
        <AuthProvider>
          <SubscriptionProvider>
            <FeatureProvider>
              <CartContextProvider>
                <SubFlowV3Provider>
                  <MoonshotAnalytics />
                  <Layout {...pageProps}>
                    <ChatbotWidget />
                    <DefaultSeo {...SEO} />
                    {(shouldDisplayProgressBar(pathname) || IS_DEV_ENV) && (
                      <ProgressBar
                        height="4px"
                        color="#536dc4"
                        options={{ easing: 'ease', speed: 500 }}
                        shallowRouting
                      />
                    )}
                    <Component {...pageProps} />
                  </Layout>
                </SubFlowV3Provider>
              </CartContextProvider>
            </FeatureProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </LoveverySWRConfig>
    </NextIntlClientProvider>
  );
}
export default withRouter(MyApp);
